import { createContext, useContext, useEffect, useMemo, useReducer } from 'react';

const CartContext = createContext(null);

function cartReducer(state, action) {
  switch (action.type) {
    case 'ADD': {
      const key = action.itemKey;
      const next = new Map(state.items);
      const existing = next.get(key);
      const qty = (existing?.quantity || 0) + (action.quantity || 1);
      next.set(key, { ...action.item, quantity: qty });
      return { items: next };
    }
    case 'REMOVE': {
      const next = new Map(state.items);
      next.delete(action.itemKey);
      return { items: next };
    }
    case 'SET_QTY': {
      const next = new Map(state.items);
      const item = next.get(action.itemKey);
      if (!item) return state;
      const qty = Math.max(1, action.quantity);
      next.set(action.itemKey, { ...item, quantity: qty });
      return { items: next };
    }
    case 'CLEAR':
      return { items: new Map() };
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const STORAGE_KEY = 'cart:v1';

  const [state, dispatch] = useReducer(
    cartReducer,
    undefined,
    () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { items: new Map() };
        const parsed = JSON.parse(raw);
        // Expecting an array of [key, item]
        if (Array.isArray(parsed)) {
          return { items: new Map(parsed) };
        }
        return { items: new Map() };
      } catch {
        return { items: new Map() };
      }
    }
  );

  // Persist cart to localStorage whenever items change
  useEffect(() => {
    try {
      const entries = Array.from(state.items.entries());
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
      // ignore storage errors (e.g., quota)
    }
  }, [state.items]);

  const totals = useMemo(() => {
    let subtotal = 0;
    for (const [, item] of state.items) {
      subtotal += item.price * item.quantity;
    }
    const taxes = +(subtotal * 0.07).toFixed(2);
    const total = +(subtotal + taxes).toFixed(2);
    const count = Array.from(state.items.values()).reduce((n, it) => n + it.quantity, 0);
    return { subtotal, taxes, total, count };
  }, [state.items]);

  const api = useMemo(() => ({
    addItem: (itemKey, item, quantity = 1) => dispatch({ type: 'ADD', itemKey, item, quantity }),
    removeItem: (itemKey) => dispatch({ type: 'REMOVE', itemKey }),
    setQuantity: (itemKey, quantity) => dispatch({ type: 'SET_QTY', itemKey, quantity }),
    clear: () => dispatch({ type: 'CLEAR' }),
  }), []);

  return (
    <CartContext.Provider value={{ items: state.items, ...api, totals }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}




